import { useEffect, useState } from 'react';
import type { InventoryCommands } from '../../types';
import {
  bulkEditFieldOperations,
  type AffectedComponentCount,
} from './bulkEditFieldOperations';

export type GetBulkEditAffectedComponentCount =
  InventoryCommands['getRuleBaedEditComponentPreview'];

const LOADING: AffectedComponentCount = { status: 'loading' };

/**
 * How many components the edit would write to, counted per field. "Only fill
 * empty cells" picks between the two numbers the same response carries, so
 * toggling it costs no request.
 *
 * The settled counts are kept alongside the field they answer for, so counts
 * left over from the previously selected field read as loading rather than as
 * the current answer.
 */
export const useAffectedComponentCount = (
  getBulkEditAffectedComponentCount: GetBulkEditAffectedComponentCount,
  fieldName: string
): AffectedComponentCount => {
  const [settled, setSettled] = useState<{
    fieldName: string;
    affectedComponentCount: AffectedComponentCount;
  } | null>(null);

  useEffect(() => {
    let isCurrentRequest = true;
    const settle = (affectedComponentCount: AffectedComponentCount) => {
      if (isCurrentRequest) {
        setSettled({ fieldName, affectedComponentCount });
      }
    };
    getBulkEditAffectedComponentCount(fieldName)
      .then(result =>
        settle(bulkEditFieldOperations.toAffectedComponentCount(result))
      )
      // A command that rejects rather than resolving an ArdoqError would
      // otherwise leave the dialog loading for good, with no way to submit and
      // nothing said about why. The command owns the logging.
      .catch(() => settle({ status: 'error' }));
    return () => {
      isCurrentRequest = false;
    };
  }, [getBulkEditAffectedComponentCount, fieldName]);

  return settled?.fieldName === fieldName
    ? settled.affectedComponentCount
    : LOADING;
};
